import { AxiosResponse } from 'axios';
import { IntlShape } from 'react-intl';
import {
  IWaypointPostion,
  IWaypointListResponseData,
  IWaypoint,
  IAddWaypointRequestData,
  IDetailWaypointResponseData,
  IFormWaypointResponseData,
  IFiltersWaypoint,
  IVerifyDeleteRequestData,
  IWindowArea,
  WAYPOINT_AREA_TYPE,
  FUEL_AREA_TYPE,
  WAYPOINT_AND_FUEL_AREA_TYPE,
  AreaTypes,
  ITypeValues,
  AreaFilterOptions,
  translateOptions,
} from './types';
import SearchService from '../../../Services/SearchService';
import { IGroupChipSelectOption } from '../../Forms/Fields/interfaces';
import { getRequest, postRequest, putRequest } from '../../../Services/HttpService';

const DEFAULT_ICON_POSITION = 220;
const DEFAULT_ICON_POSITION_FILTER_ROW = 234;
const DEFAULT_ICON_POSITION_4 = 214;
const DEFAULT_ICON_POSITION_4_FILTER_ROW = 228;
const DEFAULT_ICON_POSITION_5 = 208;
const DEFAULT_ICON_POSITION_5_FILTER_ROW = 222;
const LABEL_LENGTH_5 = 5;
const LABEL_LENGTH_4 = 4;

// const convertCoordinatesToPosition = (coordinates: any): IWaypointPostion[] => {
//   const coords = coordinates.map((coordinate: any) => {
//     const position: IWaypointPostion = {
//       latitude: coordinate[0],
//       longitude: coordinate[1],
//     };
//     return position;
//   });

//   return coords;
// };

const convertCoordinatesToPosition = (coordinates: IWaypointPostion[]): IWaypointPostion[] => {
  const coords = coordinates.map((coordinate: IWaypointPostion) => {
    const position: IWaypointPostion = {
      longitude: coordinate[0],
      latitude: coordinate[1],
    };
    return position;
  });

  return coords;
};

// const convertCenterPointToPosition = (centerPoint: any): IWaypointPostion => {
//   const position: IWaypointPostion = {
//     latitude: centerPoint[0],
//     longitude: centerPoint[1],
//   };
//   return position;
// };

const convertCenterPointToPosition = (centerPoint: [number, number]): IWaypointPostion => {
  const position: IWaypointPostion = {
    latitude: centerPoint[1],
    longitude: centerPoint[0],
  };
  return position;
};

export const getAllFromResponse = (data: IWaypointListResponseData[]): IWaypoint[] => {
  const waypoints: IWaypoint[] = data.map((waypointData: IWaypointListResponseData) => {
    const waypointItem: IWaypoint = {
      id: waypointData.id,
      name: waypointData.name,
      type: waypointData.type,
      coordinates: convertCoordinatesToPosition(waypointData.coordinates),
      center: convertCenterPointToPosition(waypointData.centerPoint),
      icon: null,
      color: null,
    };

    return waypointItem;
  });

  return waypoints;
};

export const getCoordinatesFromPaths = (paths: google.maps.LatLng[]): IWaypointPostion[] => {
  const coordinates: IWaypointPostion[] = paths.map((path: google.maps.LatLng) => {
    const data = path.toJSON();
    return {
      latitude: data.lat,
      longitude: data.lng,
    };
  });

  return coordinates;
};

export const getCenterCoordinatesFromPaths = (paths: google.maps.LatLng[]): IWaypointPostion => {
  const points = paths.map((path: google.maps.LatLng) => {
    const data = path.toJSON();
    return [data.lat, data.lng];
  });

  let sumX = 0;
  let sumY = 0;
  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    const x = point[0];
    const y = point[1];
    sumX += x;
    sumY += y;
  }

  const center: IWaypointPostion = {
    latitude: sumX / points.length,
    longitude: sumY / points.length,
  };

  return center;
};

export const convertCoordinatesToArray = (
  coordinates: IWaypointPostion[],
  addFirstAsLast = false
) => {
  const coords = coordinates.map((position: IWaypointPostion) => [
    position.longitude,
    position.latitude,
  ]);

  if (addFirstAsLast) {
    const lastCoordinate = [coordinates[0].longitude, coordinates[0].latitude];
    coords.push(lastCoordinate);
  }

  return coords;
};

export const convertCenterPointToCoordinates = (centerPoint: IWaypointPostion) => {
  const coordinates = [centerPoint.latitude, centerPoint.longitude];
  return coordinates;
};

export const getWaypointFormData = async (): Promise<IFormWaypointResponseData | null> => {
  const response: AxiosResponse | false = await getRequest(`/v2/waypoint/form`, {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    const formData: IFormWaypointResponseData = response.data;

    return formData;
  }
  return null;
};

const convertCenterPointToPositionList = (centerPoint: [number, number]): IWaypointPostion => {
  const position: IWaypointPostion = {
    latitude: centerPoint[0],
    longitude: centerPoint[1],
  };
  return position;
};

export const getWaypointList = async (
  data?: any
): Promise<{ waypoints: IWaypoint[]; filters: IFiltersWaypoint | null } | false> => {
  const response: AxiosResponse | false = await postRequest('/v2/waypoint/list', data || null, {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    const waypoints: IWaypoint[] = response.data.records.map(
      (waypointData: IWaypointListResponseData) => {
        const waypoint: IWaypoint = {
          id: waypointData.id,
          name: waypointData.name,
          type: waypointData.type,
          coordinates: undefined,
          center: convertCenterPointToPositionList(waypointData.centerPoint),
          icon: waypointData.icon,
          color: null,
          customerLabel: waypointData.customerLabel,
          categoryName: waypointData.categoryName,
          waypoint: waypointData.waypoint,
          refueling: waypointData?.refueling,
        };

        return waypoint;
      }
    );

    return {
      waypoints,
      filters: response.data.filters,
    };
  }
  return false;
};

export const getWaypointDetail = async (
  id: number
): Promise<IDetailWaypointResponseData | null> => {
  const response: AxiosResponse | false = await getRequest(`/v2/waypoint/${id}`, {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    const detailWaypoint: IDetailWaypointResponseData = {
      area: response.data.waypoint.area,
      category: response.data.waypoint.category,
      categoryId: response.data.waypoint.categoryId,
      centerPoint: convertCenterPointToPosition(response.data.waypoint.centerPoint),
      coordinates: convertCoordinatesToPosition(response.data.waypoint.coordinates),
      customerId: response.data.waypoint.customerId,
      name: response.data.waypoint.name,
      type: response.data.waypoint.type,
      typeName: response.data.waypoint.typeName,
      createdByName: response.data.waypoint.createdByName,
      createdAt: response.data.waypoint.createdAt,
      modifiedByName: response.data.waypoint.modifiedByName,
      modifiedAt: response.data.waypoint.modifiedAt,
      customerName: response.data.waypoint.customerName,
      customerLabel: response.data.waypoint.customerLabel,
      refueling: response.data.waypoint.refueling,
      waypoint: response.data.waypoint.waypoint,
    };

    return detailWaypoint;
  }
  return null;
};

export const updateWaypoint = async (id: number, data: IAddWaypointRequestData): Promise<any> => {
  const response: AxiosResponse | false = await putRequest(`/v2/waypoint/${id}/edit`, data, {
    returnAxiosResponse: true,
    returnAxioErrorResponseData: true,
  });
  if (response && response.data && response.status === 200) {
    return true;
  }
  if (response) {
    return response;
  }
  return false;
};

export const deleteWaypoint = async (id: number): Promise<boolean> => {
  const response: AxiosResponse | false = await putRequest(`/v2/waypoint/${id}/remove`, [], {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    return true;
  }
  return false;
};

export const verifyRemoveWaypoint = async (
  id: number
): Promise<IVerifyDeleteRequestData | null> => {
  const response: AxiosResponse | false = await getRequest(`/v2/waypoint/${id}/verify/remove`, {
    returnAxiosResponse: true,
  });
  if (response && response.data && response.status === 200) {
    const data: IVerifyDeleteRequestData = {
      details: response.data.details,
      references: response.data.references,
    };

    return data;
  }
  return null;
};

export const createWaypoint = async (data: IAddWaypointRequestData): Promise<any> => {
  const response: AxiosResponse | false = await postRequest(`/v2/waypoint/add`, data, {
    returnAxiosResponse: true,
    returnAxioErrorResponseData: true,
  });
  if (response && response.data && response.status === 200) {
    return response.data.waypoint;
  }
  if (response) {
    return response;
  }
  return false;
};

export const getWaypoints = (
  waypoints: IWaypoint[],
  filteredWaypoints: number[],
  filterString = ''
): IWaypoint[] => {
  if (filterString || (filteredWaypoints && filteredWaypoints.length > 0)) {
    return waypoints
      .filter((waypoint: IWaypoint) => waypoint.id && filteredWaypoints.includes(waypoint.id))
      .map((waypoint: IWaypoint) => waypoint);
  }

  return waypoints;
};

export const filterWaypoints = (
  waypointsToFilter: IWaypoint[],
  filterString: string,
  waypointTypeId: string,
  setFilteredWaypointsString: (valOrUpdater: string | ((currVal: string) => string)) => void,
  setFilteredWaypoints: (valOrUpdater: number[] | ((currVal: number[]) => number[])) => void
): void => {
  if (waypointTypeId === '' && !filterString) {
    setFilteredWaypointsString('');
    setFilteredWaypoints([]);
  }

  let tempString = filterString;

  // tempString = tempString.replace(/[`~!@#$%^&*()_|+=?;:'",<>{}[\]\\/]/gi, '');
  tempString = tempString.replace(/\s+/g, ' ').trim();
  const regexp = new RegExp(SearchService.removeAccent(tempString.toLowerCase()));

  let data: (number | undefined)[] = [];

  if (waypointTypeId === '') {
    data = waypointsToFilter
      .filter(
        (waypointItem: IWaypoint) =>
          waypointItem.name &&
          regexp.test(SearchService.removeAccent(waypointItem.name.toLowerCase()))
      )
      .map((waypointItem: IWaypoint) => waypointItem.id);
  } else if (!filterString) {
    data = waypointsToFilter
      .filter(
        (waypointItem: IWaypoint) => waypointItem.type === waypointTypeId && waypointItem.name
      )
      .map((waypointItem: IWaypoint) => waypointItem.id);
  } else {
    data = waypointsToFilter
      .filter(
        (waypointItem: IWaypoint) =>
          waypointItem.type === waypointTypeId &&
          waypointItem.name &&
          regexp.test(SearchService.removeAccent(waypointItem.name.toLowerCase()))
      )
      .map((waypointItem: IWaypoint) => waypointItem.id);
  }

  setFilteredWaypointsString(filterString);
  setFilteredWaypoints(data as number[]);
};

export const filterBinary = (searchValue: string, listArray: IWaypoint[]): IWaypoint[] => {
  if (searchValue === '') return [];
  const haystackLength = listArray.length;
  const letterNumber = searchValue.length;
  // searchValue = searchValue.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '');

  searchValue = SearchService.removeAccent(searchValue.toUpperCase());

  function findElement() {
    if (typeof listArray === 'undefined' || !haystackLength) return -1;

    let end = listArray.length - 1;
    let start = 0;

    while (start <= end) {
      const mid = Math.floor((start + end) / 2);
      const element = SearchService.removeAccent(String(listArray[mid].name).toUpperCase()).substr(
        0,
        letterNumber
      );
      // element.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '');

      if (element > searchValue) {
        end = mid - 1;
      } else if (element < searchValue) {
        start = mid + 1;
      } else {
        return mid;
      }
    }
    return -1;
  }

  /* start binary search, Get middle position */
  const getElementPosition = findElement();

  function findRangeElement() {
    let start = 0;
    let end = 0;

    for (let i = getElementPosition; i > 0; i -= 1) {
      const element = SearchService.removeAccent(String(listArray[i].name).toUpperCase()).substr(
        0,
        letterNumber
      );

      if (element !== searchValue) {
        start = i + 1;
        break;
      } else {
        start = 0;
      }
    }
    for (let i = getElementPosition; i < haystackLength; i += 1) {
      const element = SearchService.removeAccent(String(listArray[i].name).toUpperCase()).substr(
        0,
        letterNumber
      );
      if (element !== searchValue) {
        end = i;
        break;
      } else {
        end = haystackLength - 1;
      }
    }

    const result: IWaypoint[] = [];
    for (let i = start; i < end; i += 1) {
      result.push(listArray[i]);
    }

    return result;
  }

  /* get interval and return result array */
  if (getElementPosition === -1) return [];
  return findRangeElement();
};

export const filterBinarySectionList = (
  searchValue: string,
  listArray: IGroupChipSelectOption[]
): IGroupChipSelectOption[] => {
  if (searchValue === '') return [];
  const haystackLength = listArray.length;
  const letterNumber = searchValue.length;
  // searchValue = searchValue.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '');

  searchValue = SearchService.removeAccent(searchValue.toLowerCase());

  function findElement() {
    if (typeof listArray === 'undefined' || !haystackLength) return -1;

    let end = listArray.length - 1;
    let start = 0;

    while (start <= end) {
      const mid = Math.floor((start + end) / 2);
      const element = SearchService.removeAccent(String(listArray[mid].item).toLowerCase()).substr(
        0,
        letterNumber
      );
      // element.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/gi, '');

      if (element > searchValue) {
        end = mid - 1;
      } else if (element < searchValue) {
        start = mid + 1;
      } else {
        return mid;
      }
    }
    return -1;
  }

  /* start binary search, Get middle position */
  const getElementPosition = findElement();

  function findRangeElement() {
    let start = 0;
    let end = 0;

    for (let i = getElementPosition; i > 0; i -= 1) {
      const element = SearchService.removeAccent(String(listArray[i].item).toLowerCase()).substr(
        0,
        letterNumber
      );

      if (element !== searchValue) {
        start = i + 1;
        break;
      } else {
        start = 0;
      }
    }
    for (let i = getElementPosition; i < haystackLength; i += 1) {
      const element = SearchService.removeAccent(String(listArray[i].item).toLowerCase()).substr(
        0,
        letterNumber
      );
      if (element !== searchValue) {
        end = i;
        break;
      } else {
        end = haystackLength;
      }
    }

    const result: IGroupChipSelectOption[] = [];
    for (let i = start; i < end; i += 1) {
      result.push(listArray[i]);
    }

    return result;
  }

  /* get interval and return result array */
  if (getElementPosition === -1) return [];
  return findRangeElement();
};

export const createOverlayElement = (): HTMLDivElement => {
  const el = document.createElement('div');
  el.style.position = 'absolute';

  return el;
};

export const createOverlayWindow = (
  position: google.maps.LatLng,
  theContent: HTMLElement
): google.maps.OverlayView => {
  const overlay = new google.maps.OverlayView();

  overlay.onAdd = () => {
    const panes = overlay.getPanes();
    if (panes) {
      panes.markerLayer.appendChild(theContent);
    }
  };

  overlay.draw = () => {
    const divPosition = overlay.getProjection().fromLatLngToDivPixel(position);
    if (divPosition) {
      theContent.style.left = `${divPosition.x}px`;
      theContent.style.top = `${divPosition.y}px`;
    }
  };

  overlay.onRemove = () => {
    if (theContent) {
      (theContent.parentNode as HTMLElement).removeChild(theContent);
      // delete theContent;
    }
  };

  return overlay;
};

export const createInfoWindow = (
  position: google.maps.LatLng,
  createdPolygon: google.maps.Polygon
): IWindowArea => {
  const userPositionDiv = createOverlayElement();
  userPositionDiv.className = '';

  const innerDiv = document.createElement('div');

  innerDiv.className = 'e-map-pin-info customer-marked';

  innerDiv.style.transform = 'translate(-50%)';
  innerDiv.style.marginTop = '-64px';
  innerDiv.style.zIndex = '1001';

  const area = google.maps.geometry.spherical.computeArea(createdPolygon.getPath());
  const formatArea =
    area > 1000000 ? `${(area / 1000000).toFixed(2)} km&sup2` : `${area.toFixed(2)} m&sup2`;

  innerDiv.innerHTML = `<div>
  <div>
    <span class='info' >${formatArea}</span>
  </div>
  </div>`;

  userPositionDiv.appendChild(innerDiv);

  const newWindow = createOverlayWindow(position, userPositionDiv);

  return { newWindow, area };
};

export const handlePositionIcon = (
  customerLabel: (string | null)[],
  isFilterRow = false
): number => {
  const longestLabelLength = customerLabel.reduce(
    (maxLength, label) => Math.max(maxLength, label?.length ?? 0),
    0
  );

  const positionMap = {
    [LABEL_LENGTH_5]: isFilterRow ? DEFAULT_ICON_POSITION_5_FILTER_ROW : DEFAULT_ICON_POSITION_5,
    [LABEL_LENGTH_4]: isFilterRow ? DEFAULT_ICON_POSITION_4_FILTER_ROW : DEFAULT_ICON_POSITION_4,
  };

  return (
    positionMap[longestLabelLength] ||
    (isFilterRow ? DEFAULT_ICON_POSITION_FILTER_ROW : DEFAULT_ICON_POSITION)
  );
};

export const handleAreaTypeValue = (
  data: IDetailWaypointResponseData
): { value: AreaTypes; item: string } => {
  if (data.waypoint && data.refueling) {
    return { value: WAYPOINT_AND_FUEL_AREA_TYPE, item: 'enum.waypoints.area.type.waypointAndFuel' };
  }
  if (data.waypoint && !data.refueling) {
    return { value: WAYPOINT_AREA_TYPE, item: 'tripsOverview.modal.waypoint' };
  }
  return { value: FUEL_AREA_TYPE, item: 'enum.waypoints.area.type.fuel' };
};

export const getAvailableFuelOptions = (
  fuelTypeList: ITypeValues[],
  selectedFuelIds: (string | number)[],
  currentValue: string | number
): ITypeValues[] => {
  const currentStr = String(currentValue);
  const selectedIdsStr = selectedFuelIds.map(String);

  return fuelTypeList
    .map((option) => ({
      ...option,
      value: String(option.value),
    }))
    .filter((option) => {
      return option.value === currentStr || !selectedIdsStr.includes(option.value);
    });
};

export const normalizeOptions = (
  data: IFormWaypointResponseData,
  intlShape: IntlShape
): AreaFilterOptions => {
  const normalizeList = (list: Array<{ value: string | number; item: string }>) =>
    list.map((item) => ({
      ...item,
      value: String(item.value),
    }));

  return {
    categoryId: Array.isArray(data.categoryId)
      ? normalizeList(data.categoryId)
      : normalizeList(data.categoryId?.values ?? []),

    customerId: Array.isArray(data.customerId)
      ? normalizeList(data.customerId)
      : normalizeList(data.customerId?.values ?? []),

    type: Array.isArray(data.type)
      ? normalizeList(data.type)
      : normalizeList(data.type?.values ?? []),

    refuelingTypeList: normalizeList(data.refuelingTypeList),
    areaType: translateOptions(intlShape),
  };
};
